/**
 * MyPodOps Studio V2 - API Client
 *
 * Centralized API client for all backend communication.
 * Handles sessions, uploads, recordings, and invites.
 */

// API Configuration
// In development, use Vite proxy to avoid CORS issues
// In production, use the full API Gateway URL
const IS_DEV = import.meta.env.DEV;
const PROD_API_BASE = import.meta.env.VITE_API_BASE || 'https://jtpw1iado5.execute-api.us-east-2.amazonaws.com/prod';
const API_BASE = IS_DEV ? '/api' : PROD_API_BASE;

// Use existing /studio endpoints (not /studio-v2 which doesn't exist on backend)
const STUDIO_PREFIX = '/studio';

// Log API configuration for debugging
if (IS_DEV) {
  console.log('[API] Development mode - using Vite proxy at /api');
} else {
  console.log('[API] Production mode - using', PROD_API_BASE);
}

// Types
export interface Session {
  sessionId: string;
  hostId: string;
  hostName: string;
  sessionName: string;
  status: 'waiting' | 'live' | 'recording' | 'ended';
  videoSDKRoomId: string;
  settings: SessionSettings;
  participantCount: number;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
}

export interface SessionSettings {
  waitingRoom: boolean;
  maxParticipants: number;
  recordingQuality: '4k' | '1080p' | '720p' | 'audio_only';
  allowScreenShare: boolean;
  allowChat: boolean;
}

export interface Participant {
  participantId: string;
  sessionId: string;
  name: string;
  email?: string;
  role: 'host' | 'co-host' | 'guest';
  status: 'waiting' | 'admitted' | 'recording' | 'left';
  joinedAt: string;
  videoSDKToken?: string;
}

export interface Recording {
  recordingId: string;
  sessionId: string;
  hostId: string;
  sessionName: string;
  participantId: string;
  participantName: string;
  mediaType: 'video' | 'audio' | 'screen';
  quality: string;
  status: 'uploading' | 'processing' | 'ready' | 'failed';
  fileSize: number;
  duration?: number;
  s3Key: string;
  downloadUrl?: string;
  createdAt: string;
  completedAt?: string;
}

export interface Upload {
  uploadId: string;
  sessionId: string;
  participantId: string;
  recordingId: string;
  s3UploadId: string;
  s3Key: string;
  totalChunks: number;
  completedChunks: number;
  totalSize: number;
  uploadedSize: number;
  status: 'in_progress' | 'completing' | 'completed' | 'failed' | 'aborted';
}

export interface Invite {
  inviteId: string;
  sessionId: string;
  inviteCode: string;
  inviteUrl: string;
  email?: string;
  guestName?: string;
  role: 'co-host' | 'guest';
  maxUses: number;
  usedCount: number;
  status: 'active' | 'used' | 'expired' | 'revoked';
  expiresAt: string;
  createdAt: string;
}

// API Response Types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Error class
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Auth helper - check all possible token storage keys
export function getAuthToken(): string | null {
  return (
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('auth_token')
  );
}

// Request helper for standard REST endpoints
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${STUDIO_PREFIX}${endpoint}`;
  const token = getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch {
    throw new ApiError('Invalid response from server', response.status);
  }

  if (!response.ok || !data.success) {
    throw new ApiError(
      data.error || data.message || 'Request failed',
      response.status,
      data.error
    );
  }

  return data.data as T;
}

// Request helper for action-based /studio endpoint
async function studioActionRequest<T>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const url = `${API_BASE}${STUDIO_PREFIX}`;
  const token = getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  });

  let data: Record<string, unknown>;
  try {
    data = await response.json();
  } catch {
    throw new ApiError('Invalid response from server', response.status);
  }

  if (!response.ok) {
    throw new ApiError(
      (data.error as string) || (data.message as string) || 'Request failed',
      response.status
    );
  }

  return data as T;
}

// ============================================
// Session API
// ============================================

export const sessionsApi = {
  /**
   * Create a new recording session
   * Uses the existing /studio endpoint with action=create-meeting
   * Then fetches a VideoSDK token
   */
  async create(params: {
    hostId: string;
    hostName: string;
    sessionName: string;
    settings?: Partial<SessionSettings>;
  }): Promise<Session & { videoSDKToken: string }> {
    // Step 1: Create the meeting room using existing endpoint
    const meetingResponse = await studioActionRequest<{
      roomId?: string;
      meetingId?: string;
      message?: string;
    }>('create-meeting');

    const roomId = meetingResponse.roomId || meetingResponse.meetingId;
    if (!roomId) {
      throw new ApiError('Failed to create meeting room', 500);
    }

    // Step 2: Get VideoSDK token for the host
    const tokenResponse = await fetch(`${API_BASE}${STUDIO_PREFIX}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` }),
      },
      body: JSON.stringify({
        meetingId: roomId,
        uid: params.hostId,
      }),
    });

    let tokenData: { token?: string };
    try {
      tokenData = await tokenResponse.json();
    } catch {
      throw new ApiError('Failed to get VideoSDK token', tokenResponse.status);
    }

    if (!tokenResponse.ok || !tokenData.token) {
      throw new ApiError('Failed to get VideoSDK token', tokenResponse.status);
    }

    // Return session object with VideoSDK details
    const sessionId = `session-${Date.now()}`;
    return {
      sessionId,
      hostId: params.hostId,
      hostName: params.hostName,
      sessionName: params.sessionName,
      status: 'waiting',
      videoSDKRoomId: roomId,
      videoSDKToken: tokenData.token,
      settings: {
        waitingRoom: params.settings?.waitingRoom ?? true,
        maxParticipants: params.settings?.maxParticipants ?? 10,
        recordingQuality: params.settings?.recordingQuality ?? '1080p',
        allowScreenShare: params.settings?.allowScreenShare ?? true,
        allowChat: params.settings?.allowChat ?? true,
      },
      participantCount: 0,
      createdAt: new Date().toISOString(),
    };
  },

  /**
   * Get session details
   */
  async get(sessionId: string): Promise<Session & { participants: Participant[] }> {
    return apiRequest(`/sessions/${sessionId}`);
  },

  /**
   * List user's sessions
   */
  async list(params?: {
    hostId?: string;
    status?: string;
    limit?: number;
    startKey?: string;
  }): Promise<{ sessions: Session[]; nextKey?: string }> {
    const query = new URLSearchParams();
    if (params?.hostId) query.set('hostId', params.hostId);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.startKey) query.set('startKey', params.startKey);

    const queryString = query.toString();
    return apiRequest(`/sessions${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Update session (settings, status)
   */
  async update(
    sessionId: string,
    updates: {
      sessionName?: string;
      settings?: Partial<SessionSettings>;
      status?: 'live' | 'recording' | 'ended';
    }
  ): Promise<Session> {
    return apiRequest(`/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Join a session
   * Gets a VideoSDK token for the participant
   */
  async join(
    sessionId: string,
    params: {
      participantId: string;
      name: string;
      email?: string;
      role?: 'co-host' | 'guest';
      inviteCode?: string;
      videoSDKRoomId?: string; // Pass the room ID if known
    }
  ): Promise<Participant & { videoSDKToken: string; session: Session }> {
    // Get VideoSDK token for participant
    const roomId = params.videoSDKRoomId || sessionId;
    const tokenResponse = await fetch(`${API_BASE}${STUDIO_PREFIX}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` }),
      },
      body: JSON.stringify({
        meetingId: roomId,
        uid: params.participantId,
      }),
    });

    let tokenData: { token?: string };
    try {
      tokenData = await tokenResponse.json();
    } catch {
      throw new ApiError('Failed to get VideoSDK token', tokenResponse.status);
    }

    if (!tokenResponse.ok || !tokenData.token) {
      throw new ApiError('Failed to get VideoSDK token', tokenResponse.status);
    }

    // Return participant and session info
    return {
      participantId: params.participantId,
      sessionId,
      name: params.name,
      email: params.email,
      role: params.role || 'guest',
      status: 'admitted',
      joinedAt: new Date().toISOString(),
      videoSDKToken: tokenData.token,
      session: {
        sessionId,
        hostId: '',
        hostName: '',
        sessionName: '',
        status: 'live',
        videoSDKRoomId: roomId,
        settings: {
          waitingRoom: false,
          maxParticipants: 10,
          recordingQuality: '1080p',
          allowScreenShare: true,
          allowChat: true,
        },
        participantCount: 1,
        createdAt: new Date().toISOString(),
      },
    };
  },

  /**
   * Leave a session
   */
  async leave(
    sessionId: string,
    participantId: string
  ): Promise<{ message: string }> {
    return apiRequest(`/sessions/${sessionId}/leave`, {
      method: 'POST',
      body: JSON.stringify({ participantId }),
    });
  },

  /**
   * Admit a participant from waiting room
   */
  async admitParticipant(
    sessionId: string,
    participantId: string,
    admit: boolean
  ): Promise<Participant> {
    return apiRequest(`/sessions/${sessionId}/admit`, {
      method: 'POST',
      body: JSON.stringify({ participantId, admit }),
    });
  },
};

// ============================================
// Upload API
// ============================================

export const uploadsApi = {
  /**
   * Initiate a multipart upload
   */
  async initiate(params: {
    sessionId: string;
    participantId: string;
    participantName: string;
    hostId: string;
    sessionName: string;
    filename: string;
    contentType: string;
    totalSize: number;
    totalChunks: number;
    mediaType: 'video' | 'audio' | 'screen';
    quality: string;
  }): Promise<Upload & { recordingId: string }> {
    return apiRequest('/uploads/initiate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  /**
   * Get presigned URL for chunk upload
   */
  async getChunkUrl(
    uploadId: string,
    partNumber: number
  ): Promise<{ uploadUrl: string; partNumber: number }> {
    return apiRequest(`/uploads/${uploadId}/chunk-url`, {
      method: 'POST',
      body: JSON.stringify({ partNumber }),
    });
  },

  /**
   * Record that a chunk was uploaded
   */
  async chunkComplete(
    uploadId: string,
    partNumber: number,
    etag: string,
    chunkSize: number
  ): Promise<{ completedChunks: number; uploadedSize: number }> {
    return apiRequest(`/uploads/${uploadId}/chunk-complete`, {
      method: 'POST',
      body: JSON.stringify({ partNumber, etag, chunkSize }),
    });
  },

  /**
   * Complete the multipart upload
   */
  async complete(
    uploadId: string,
    duration?: number
  ): Promise<Recording> {
    return apiRequest(`/uploads/${uploadId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ duration }),
    });
  },

  /**
   * Abort the upload
   */
  async abort(uploadId: string): Promise<{ message: string }> {
    return apiRequest(`/uploads/${uploadId}/abort`, {
      method: 'POST',
    });
  },

  /**
   * Get upload status
   */
  async getStatus(uploadId: string): Promise<Upload> {
    return apiRequest(`/uploads/${uploadId}`);
  },
};

// ============================================
// Recordings API
// ============================================

export const recordingsApi = {
  /**
   * List recordings
   */
  async list(params?: {
    hostId?: string;
    sessionId?: string;
    status?: string;
    limit?: number;
    startKey?: string;
  }): Promise<{ recordings: Recording[]; nextKey?: string }> {
    const query = new URLSearchParams();
    if (params?.hostId) query.set('hostId', params.hostId);
    if (params?.sessionId) query.set('sessionId', params.sessionId);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.startKey) query.set('startKey', params.startKey);

    const queryString = query.toString();
    return apiRequest(`/recordings${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get recording details with download URL
   */
  async get(recordingId: string): Promise<Recording & { downloadUrl: string }> {
    return apiRequest(`/recordings/${recordingId}`);
  },

  /**
   * Delete a recording
   */
  async delete(recordingId: string): Promise<{ message: string }> {
    return apiRequest(`/recordings/${recordingId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// Invites API
// ============================================

export const invitesApi = {
  /**
   * Generate an invite link
   */
  async generate(params: {
    sessionId: string;
    hostId: string;
    role?: 'co-host' | 'guest';
    maxUses?: number;
    expiresIn?: number;
    guestName?: string;
  }): Promise<Invite> {
    return apiRequest('/invites/generate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  /**
   * Send email invitation
   */
  async sendEmail(params: {
    sessionId: string;
    hostId: string;
    hostName: string;
    sessionName: string;
    guestEmail: string;
    guestName?: string;
    role?: 'co-host' | 'guest';
    personalMessage?: string;
    scheduledTime?: string;
  }): Promise<Invite & { emailSent: boolean }> {
    return apiRequest('/invites/send-email', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  /**
   * Validate an invite code
   */
  async validate(inviteCode: string): Promise<{
    valid: boolean;
    invite?: Invite;
    session?: Session;
  }> {
    return apiRequest('/invites/validate', {
      method: 'POST',
      body: JSON.stringify({ inviteCode }),
    });
  },

  /**
   * List invites for a session
   */
  async listForSession(sessionId: string): Promise<{ invites: Invite[] }> {
    return apiRequest(`/sessions/${sessionId}/invites`);
  },

  /**
   * Revoke an invite
   */
  async revoke(inviteId: string): Promise<{ message: string }> {
    return apiRequest(`/invites/${inviteId}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// Unified API Export
// ============================================

export const api = {
  sessions: sessionsApi,
  uploads: uploadsApi,
  recordings: recordingsApi,
  invites: invitesApi,
};

export default api;
